<template>
  <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <p class="modal-title">
          <i class="fas fa-cog mr-2"></i>
          <span class="font-weight-bold">Settings</span>
        </p>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <div class="p-3">
          <!-- User Preferences Section -->
          <div class="row">
            <h4 class="col-4">User Preferences</h4>
          </div>
          <div class="row">
            <div class="col-4">
              <div class="form-group">
                <label>Default Home View</label>
                <select v-model="modal.view" class="form-control form-control-sm" v-on:change="modal.change(`view`)">
                  <option v-for="(o, i) in modal.options(`view`)" :key="i">
                    <span v-text="o"></span>
                  </option>
                </select>
                <small class="form-text text-muted">Your default home screen </small>
              </div>
            </div>
            <div class="col"></div>
          </div>
          <hr>

          <!-- Features Section -->
          <div class="row">
            <h4 class="col-4">Features</h4>
          </div>
          <div class="row mb-3">
            <div class="col">
              <div v-for="(value, feature) in modal.features" :key="feature" class="d-flex align-items-center mb-2">
                <i class="fas mr-2" :class="value ? 'fa-check-circle text-primary' : 'fa-times-circle text-muted'"></i>
                <span>{{ formatFeatureLabel(feature) }}</span>
              </div>
            </div>
          </div>
          <hr>

          <!-- Contract Defaults Section -->
          <div class="row">
            <h4 class="col-4">Contract Defaults</h4>
          </div>
          <div class="row">
            <div class="col-4">
              <div class="form-group">
                <label>Default Billing Type</label>
                <select v-model="modal.contractsDefault" class="form-control form-control-sm" v-on:change="modal.change(`contractsDefault`)">
                  <option v-for="(option, i) in modal.options(`contractsDefault`)" :key="i" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <small class="form-text text-muted">Applies to new contracts</small>
              </div>
            </div>
            <div class="col"></div>
          </div>
          <hr>

          <!-- Security Section -->
          <div class="row">
            <h4 class="col-4">Security</h4>
          </div>
          <div class="row">
            <div class="col-12">
              <div class="form-group">
                <label><strong>Multi-Factor Authentication (MFA)</strong></label>
                <small class="form-text text-muted mb-3">Add an extra layer of security to your account using an
                  authenticator app (Google Authenticator, Authy, etc).</small>
                <div v-if="mfa.active">
                  <div class="alert alert-success row align-items-center p-2 m-0">
                    <div class="col-auto pr-0"><i class="fas fa-lock"></i></div>
                    <div class="col">MFA is <strong>enabled</strong> on your account.</div>
                    <div class="col-auto pl-0"><button class="btn btn-sm btn-secondary" @click="disableMFA"
                        :disabled="mfa.pending">Disable</button></div>
                  </div>
                </div>
                <div v-else>
                  <div v-if="mfa.qr">

                    <div class="row mb-3">
                      <div class="col-6">1. Scan this QR code with your authenticator app:</div>
                      <div class="col-auto mx-auto d-block">
                        <img :src="mfa.qr" alt="MFA QR Code"
                          style="max-width:150px; border:1px solid #eee; background:#fff; padding:8px; border-radius:8px;" />
                      </div>
                    </div>
                    <div class="row justify-content-center">
                      <div class="col-6">
                        <label>2. Enter the 6-digit code from your app:</label>
                      </div>
                      <div class="col-6">
                        <div class="input-group input-group-sm">
                          <input v-model="mfa.code" class="form-control text-center" maxlength="6"
                            placeholder="123456" />
                          <div class="input-group-append">
                            <button class="btn btn-outline-primary" @click="verifyMFA" :disabled="mfa.pending">
                              <span v-if="!mfa.pending">Verify</span>
                              <span v-else><i class="fas fa-spinner fa-spin"></i></span>
                            </button>
                            <button class="btn btn-sm btn-outline-danger" @click="cancelMFA"
                              :disabled="mfa.pending">Cancel</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <button class="btn btn-sm btn-primary" @click="setupMFA" :disabled="mfa.pending">Enable
                      MFA</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <hr />

          <!-- Integrations Section -->
          <div class="row">
            <h4 class="col-4">Integrations</h4>
          </div>
          <div class="row">
            <div class="col">
              <div class="form-group">
                <label><strong>Redtail CRM</strong></label>
                <div class="custom-control custom-switch">
                  <input type="checkbox" class="custom-control-input" id="redTailIntegrationSwitch"
                    @change="changeRedtailVars" :checked="rtUserAuthKey" :disabled="wbAuthenticated">
                  <label class="custom-control-label" for="redTailIntegrationSwitch">Allow bi-directional data exchange
                    with Redtail</label>
                </div>
                <div v-if="rtShowInput" class="row pt-2">
                  <div class="col-10">
                    <input type="text" class="form-control form-control-sm" placeholder="User Auth Key"
                      v-model="rtUserAuthKey">
                    <small class="form-text text-muted">Copy the UserAuthKey from Redtail Imaging and paste it
                      here</small>
                  </div>
                  <div class="col-2" style="padding-left: 0;">
                    <button class="btn btn-sm btn-primary" @click="saveRTUserAuthKey">Save</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="form-group">
                <label><strong>Wealthbox CRM</strong></label>
                <div class="row align-items-center">
                  <div class="col-9">
                    Allow bi-directional data exchange with Wealthbox
                  </div>
                  <div class="col-3 text-right">
                    <button v-show="wbAuthenticated" class="btn btn-sm btn-secondary"
                      @click="disconnectOauth">Disconnect</button>
                    <button v-show="!wbAuthenticated" :disabled="rtShowInput" class="btn btn-sm btn-primary"
                      @click="openAuthPopup">Connect</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* div.qrcode {
  margin-top: -8px;
} */
p.modal-title i,
p.modal-title span {
  opacity: 0.7;
}
</style>

<script>
import { mfa } from "../component";
import { Api, lib } from "../factory";
// import { qrcode } from "../npm";
import { alert, session } from "../service";

export default {
  get components() {
    return {
      mfa,
    };
  },
  data() {
    return {
      modal: ``,
      rtShowInput: null,
      rtUserAuthKey: null,
      wbAuthenticated: false,
      authWindow: null,
      mfa: {  // MFA specific
          active: false,
          pending: false,
          qr: '',
          code: '',
          error: ''
        },
    };
  },
  created() {
    this.init();
  },
  mounted() {
    window.addEventListener('message', this.handleAuthMessage);
    // Verify Wealthbox connection status when settings modal loads
    this.verifyWealthboxConnection();
  },
  methods: {
    saveRTUserAuthKey() {
      this.modal._save(`redtail`, 
      { redTailKey: this.rtUserAuthKey })
    },
    changeRedtailVars(event) {
      this.rtShowInput = event.target.checked;
      if (event.target.checked) return

      // only update the backend when we are disconnecting from Redtail
      this.rtUserAuthKey = null
      this.saveRTUserAuthKey()

    },
    formatFeatureLabel(feature) {
      // Hardcoded labels for specific features
      if (feature === 'cashflowAdjustment') {
        return 'Smart Recon';
      }
      
      return feature
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase());
    },
    async verifyWealthboxConnection() {
      try {
        const firm = session.get(`state`).firm;
        
        // Skip if no Wealthbox integration configured
        if (!firm.providerTokensId) {
          this.wbAuthenticated = false;
          return;
        }

        // Call the verification endpoint
        const { data } = await Api.get(`crm/verify?firmId=${firm._id}`);
        
        if (data.valid) {
          this.wbAuthenticated = true;
          console.log('Wealthbox connection verified.');
        } else {
          // Connection is invalid - likely OAuth was revoked
          this.wbAuthenticated = false;
          console.warn('Wealthbox connection invalid:', data.reason);
          alert.warning(`Wealthbox connection issue: ${data.reason}. Please reconnect.`, 3000);
        }
      } catch (error) {
        console.error('Error verifying Wealthbox connection:', error);
        this.wbAuthenticated = false;
      }
    },
    async disconnectOauth() {

      const firm = session.get(`state`).firm
      try {
        const { data } = await Api.delete(`crm/tokens/${firm.providerTokensId}`);

        if (!data.affectedRows)
          return alert.error(`No WealthBox integration was found.`, 2e3)

        // nullify data
        this.wbAuthenticated = false;
        firm.providerTokensId = null

      } catch(error) {
          alert.error(`There was an error disconnecting from WealthBox CRM. Please try again.`, 2e3)
          console.log(error)
      }

    },
    openAuthPopup() {

      const clientId = 'jaUFatLJ2hg-qCgEKjQWNjAX25tHeazhWnJNrCKsBtg'
      const redirectURI = 'https://app.smartkx.io/wb/auth-callback'
      const url = `https://app.crmworkspace.com/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectURI}&response_type=code&scope=login+data`;

      const width = 600;
      const height = 600;
      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      this.authWindow = window.open(url, 'auth',
        `width=${width},height=${height},left=${left},top=${top}`);

    },
    async handleAuthMessage(event) {
      if (event.data.type !== 'wb-oauth-response')
        return

      // Capture the code from the message
      const code = event.data.code;
      console.log('Received code:', code);

      // Close the pop-up window
      this.authWindow.close();
      // You can now use the 'code' in your application for further OAuth2 token exchange

      const state = session.get(`state`)
      const providerTokensId = state.firm.providerTokensId
      var { data } = await Api.post(`crm/tokens`, {
        authCode: code,
        providerTokensId: providerTokensId
      });
      console.log("oauth resp =>", data)
      if (data.providerTokensId) {
        state.firm.providerTokensId = data.providerTokensId
        // Verify the connection was successful
        await this.verifyWealthboxConnection();
      }
    },
    // MFA related methods
    async setupMFA() {
      this.mfa.pending = true;
      this.mfa.error = '';
      try {
        const { data } = await Api.post('users/mfa', { action: 'setup' });
        this.mfa.qr = data.qr;
        this.mfa.otpauth_url = data.otpauth_url;
        // Update state.user.mfa to match backend
        const state = session.get(`state`)
        state.user.mfa = {
          active: false,
          secret: data.otpauth_url.split('secret=')[1].split('&')[0],
          token: ''
        };
      } catch (e) {
        this.mfa.error = e.message || 'Failed to start MFA setup.';
        alert.error(e.message, 3000);
      } finally {
        this.mfa.pending = false;
      }
    }, // MFA related methods
    async verifyMFA() {
      this.mfa.pending = true;
      this.mfa.error = '';
      try {
        const { data } = await Api.post('users/mfa',
          {
            action: 'verify',
            code: this.mfa.code
          });

        if (data.success) {
          this.mfa.active = true;
          this.mfa.qr = '';
          this.mfa.code = '';
          alert.message('MFA enabled successfully!', 2000);
          state.user.mfa.active = true;
        }
      } catch (e) {
        this.mfa.error = e.message || 'Invalid code.';
      } finally {
        this.mfa.pending = false;
      }
    },
    async disableMFA() {
      this.mfa.pending = true;
      this.mfa.error = '';
      try {
        const { data } = await Api.post('users/mfa', { action: 'disable' });
        if (data.success) {
          this.mfa.active = false;
          alert.message('MFA disabled.', 2000);
          state.user.mfa = { active: false, secret: '', token: '' };
        }
      } catch (e) {
        this.mfa.error = e.message || 'Failed to disable MFA.';
      } finally {
        this.mfa.pending = false;
      }
    },
    cancelMFA() {
      this.mfa.qr = '';
      this.mfa.code = '';
      this.mfa.error = '';
    },
    init() {
      var _this = this
      var state = session.get(`state`);
      var modal = {
        features: {},
        async _save(type, d) {
          try {
            switch (type) {
              case `user`:
                var { _id: userId } = state.user;
                await Api.put(`/users/${userId}`, d);
                break;
              case `firm`:
                var { _id: firmId } = state.firm;
                await Api.put(`/firms/${firmId}`, d);
                alert.message("Firm settings updated successfully.", 15e2);
                break;
              case `redtail`:
                var { _id: firmId } = state.firm;
                await Api.put(`/firms/${firmId}`, d);
                state.firm.redTailKey = d.redTailKey 
                alert.message("Auth key was successfully updated.", 15e2);
                break;
              default:
                throw new Error(`Invalid save type, ${type}!`);
            }
          } catch (e) {
            console.error(e);
            alert.error(e.message);
          }
        },
        change(type) {
          switch (type) {
            case `view`:
              if (!state.user.settings) state.user.settings = {};
              state.user.settings.view = this.view.toLowerCase();
              var { settings } = state.user;
              this._save(`user`, { settings });
              break;
            case `contractsDefault`:
              // Don't save if empty value (placeholder) is selected
              if (!this.contractsDefault) return;
              
              this._save(`firm`, { contractsDefault: this.contractsDefault });
              // Parse the JSON string and update state so dropdowns reflect change immediately
              try {
                state.firm.contractsDefault = JSON.parse(this.contractsDefault);
              } catch (e) {
                state.firm.contractsDefault = this.contractsDefault;
              }
              break;
            default:
              throw new Error(`Invalid change type, ${type}!`);
          }
        },
        click(type) {
          switch (type) {
            case `done`:
              this._enable(true);
              break;
            default:
              throw new Error(`Invalid click type, ${type}!`);
          }
        },
        options(type) {
          switch (type) {
            case `view`:
              return [`Firm`, `Clients`];
            case `contractsDefault`:
              return [
                { label: '-- Select Billing Type --', value: '' },
                { label: 'Quarterly Advance, Latest', value: '{"schedule":"Quarterly","type":"advance","values":"Latest"}' },
                { label: 'Quarterly Advance, Average Daily', value: '{"schedule":"Quarterly","type":"advance","values":"Average Daily"}' },
                { label: 'Quarterly Arrears, Latest', value: '{"schedule":"Quarterly","type":"arrears","values":"Latest"}' },
                { label: 'Quarterly Arrears, Average Daily', value: '{"schedule":"Quarterly","type":"arrears","values":"Average Daily"}' },
                { label: 'Monthly Advance, Latest', value: '{"schedule":"Monthly","type":"advance","values":"Latest"}' },
                { label: 'Monthly Arrears, Latest', value: '{"schedule":"Monthly","type":"arrears","values":"Latest"}' },
                { label: 'Monthly Arrears, Average Daily', value: '{"schedule":"Monthly","type":"arrears","values":"Average Daily"}' }
              ];
            default:
              throw new Error(`Invalid options type, ${type}!`);
          }
        },
        init() {
          this.view =
            state.user.hasOwnProperty(`settings`) && state.user.settings.view
              ? lib.string.capitalize(state.user.settings.view)
              : this.options(`view`)[0];

          // Initialize contractsDefault - MariaDB already parsed it from JSON
          let contractsDefault = state.firm.contractsDefault || ``;
          if (typeof contractsDefault === 'object') {
            // Convert back to JSON string for dropdown
            contractsDefault = JSON.stringify(contractsDefault);
          }
          this.contractsDefault = contractsDefault;

          // Initialize features
          this.features = Object.assign({}, state.firm.features)
          // Redtail specific
          _this.rtUserAuthKey = state.firm.redTailKey
          _this.rtShowInput = state.firm.redTailKey ? true : false

          // Wealthbox specific
          _this.wbAuthenticated = state.firm.providerTokensId ? true : false

          // MFA specific
          _this.mfa.active = state.user.mfa && state.user.mfa.active;

          return this;
        },
        user: ``,
        view: ``,
        contractsDefault: ``,
        async setFeature(featureName, toggle) {
          if (!state.firm.features) state.firm.features = {};
          this.features[featureName] = toggle;
          state.firm.features[featureName] = toggle;
          var { features, _id: firmId } = state.firm;
          await Api.put(`/firms/${firmId}`, { features });
        },
        featuresLabel(featureName, toggle) {
          return `btn btn-light ${this.features[featureName] == toggle ? "active" : ""}`
        }
      };
      this.modal = modal.init();
    },
  },
};
</script>
